import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { NgxDropzoneChangeEvent, NgxDropzoneModule } from 'ngx-dropzone';
import { NgxSpinnerService } from 'ngx-spinner';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { toSignal } from '@angular/core/rxjs-interop';

import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';

import {
  FileUploadService,
  FileUploadResult,
  FileUploadStatus
} from '../../services/file-upload/file-upload.service';
import { NgClass } from '@angular/common';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { WebScanningDialogComponent } from '../web-scanning-dialog/web-scanning-dialog.component';
import { Button } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  imports: [
    NgxDropzoneModule,
    NgClass,
    Button,
    LoadingOverlayComponent,
    WebScanningDialogComponent,
    TranslatePipe
  ]
})
export class FileUploaderComponent implements AfterViewInit {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private spinner = inject(NgxSpinnerService);
  private featureFlagService = inject(FeatureFlagService);
  private userPrivilegesService = inject(UserPrivilegesService);
  private fileUploadService = inject(FileUploadService);

  @Output() uploadedDocumentIdsEvent = new EventEmitter<string[]>();
  @Output() uploadStatusEvent = new EventEmitter<FileUploadStatus[]>();
  @Output() errorEvent = new EventEmitter<string>();
  @Output() scanRequest = new EventEmitter<void>();
  @Output() filesStaged = new EventEmitter<File[]>();
  @Input() tenantId: string = '';
  @Input() showNoFilesError = false;
  @Input() isTranscriptFiling: boolean = false;
  @Input() hideScanning: boolean = false;
  @Input() ignoreScanPrivileges: boolean = false;
  @Input() uploadListReview: boolean = false;

  files: FileUploadStatus[] = [];
  uploadedDocumentIds: string[] = [];
  isProcessing: boolean = false;
  accessWebScanning = toSignal(
    this.featureFlagService.getFlag('feature-web-capture-scanning', false)
  );
  hasScanPrivileges = toSignal(this.userPrivilegesService.canPerformAction('ETRIEVE_CONTENT_SCAN'));
  showScanDialog = false;

  /**
   * Removes ngx-dropzone's hidden file input from the tab order; the Browse Files button is the keyboard entry point
   */
  ngAfterViewInit(): void {
    this.host.nativeElement
      .querySelector('ngx-dropzone input[type="file"]')
      ?.setAttribute('tabindex', '-1');
  }

  /**
   *
   */
  startScanProcess() {
    this.showScanDialog = true;
  }

  /**
   * Handles files received from the scanning process.
   * @param files - Array of scanned File objects to be processed.
   */
  onScannedFiles(files: File[]) {
    this.onSelect({ source: null as any, addedFiles: files, rejectedFiles: [] });
  }

  /**
   * Handles file selection from the dropzone by reading the file content and processing the document
   * @param event - The dropzone change event containing the selected files
   */
  onSelect(event: NgxDropzoneChangeEvent): void {
    if (this.uploadListReview) {
      if (event.addedFiles.length > 0) {
        this.filesStaged.emit(event.addedFiles);
      }
      return;
    }
    if (event.addedFiles.length > 0) {
      this.uploadedDocumentIds = [];
      this.files = [];
      event.addedFiles.forEach((file, index) =>
        this.files.push({
          file: file,
          status: 'uploading',
          index: index
        })
      );
      this.uploadStatusEvent.emit(this.files);
      this.processFiles();
    }
  }

  /**
   * Processes all files by uploading them to the document management system
   */
  private processFiles(): void {
    const filesToUpload = this.files.map(f => f.file);
    const emittedErrors = new Set<number>();

    this.fileUploadService
      .uploadFiles(filesToUpload, this.tenantId, this.isTranscriptFiling)
      .subscribe({
        next: (result: FileUploadResult) => {
          this.files = result.statuses;
          this.uploadedDocumentIds = result.uploadedIds;
          this.uploadStatusEvent.emit(this.files);

          result.statuses.forEach(status => {
            if (status.status === 'failed' && status.error && !emittedErrors.has(status.index)) {
              emittedErrors.add(status.index);
              this.errorEvent.emit(status.error);
            }
          });
        },
        complete: () => {
          if (this.uploadedDocumentIds.length > 0) {
            this.uploadedDocumentIdsEvent.emit(this.uploadedDocumentIds);
          }
        },
        error: error => {
          this.errorEvent.emit(error);
        }
      });
  }

  /**
   * Determines if the dropzone should be disabled based on current processing state
   * @returns True if dropzone should be disabled, false otherwise
   */
  shouldDisableDropzone(): boolean {
    return this.isProcessing || this.files.some(file => file.status === 'uploading');
  }

  /**
   * Handles file selection from the file input element
   * @param event - The input change event containing selected files
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const addedFiles = Array.from(input.files);
      this.onSelect({ source: null as any, addedFiles, rejectedFiles: [] });
      input.value = '';
    }
  }
}
